import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { APIList } from 'src/interfaces'
import { Entities } from 'src/enums'
import { ProductTypeTip } from 'src/api'
import { useApiInstance } from './useApiInstance'
import {
  buildRequestParams,
  ObjectType,
  transformPage,
  transformPerPage,
} from './helpers'

interface RequestParams {
  params?: Partial<AdminDefaultTipsGetParams>;
  paramsTransform?: Array<(params: Record<string, any>) => Record<string, any>>;
  options?: Omit<
    UseQueryOptions<unknown, Error, APIList<ProductTypeTip>>,
    'queryKey' | 'queryFn'
  >;
}

interface AdminDefaultTipsGetParams {
  limit: number;
  offset: number;
  sort: string;
  tipTypeId: number;
  productTypeId: number;
}

const schema: ObjectType['schema'] = {
  limit: 'number',
  offset: 'number',
  sort: 'string',
  tipTypeId: 'number',
  productTypeId: 'number',
}

export const useDefaultTipsRequest = ({
  params = {},
  paramsTransform = [],
  options,
}: RequestParams = {}) => {
  const api = useApiInstance()

  const transformedParams = [
    transformPerPage,
    transformPage,
    ...paramsTransform,
  ].reduce((param, transform) => transform(param), params)

  const queryParams = buildRequestParams<AdminDefaultTipsGetParams>(
    transformedParams,
    schema,
  )

  return useQuery<unknown, Error, APIList<ProductTypeTip>>(
    [Entities.ProductTips, JSON.stringify(transformedParams)],
    async () => {
      const { limit, offset, sort, tipTypeId, productTypeId } = queryParams
      const { data } = await api.v1AdminProductsTipsDefaultGet(
        limit,
        offset,
        sort,
        tipTypeId,
        productTypeId,
      )

      return data
    },
    {
      enabled: Object.keys(transformedParams).length > 0,
      ...options,
    },
  )
}
